import React from 'react';
import * as THREE from 'three';

// «سایه‌ی کنج» (ambient occlusion ساده و سبک) برای حسِ عمق: در اتاقِ واقعی، جایی که دیوار به کف و سقف می‌رسد
// و گوشه‌های اتاق، کمی تیره‌تر است — چون نورِ محیطی کمتری به آن‌جا می‌رسد. این یکی از قوی‌ترین نشانه‌هایی
// است که چشم با آن عمق و فاصله را می‌فهمد. به‌جای یک افکتِ سنگینِ پس‌پردازش (SSAO، که روی گوشی کند است)،
// این‌جا نوارهای نازکِ نیمه‌شفاف با گرادیانِ نرم دقیقاً در همین محل‌ها کشیده می‌شوند.
//
// AoStrip یک صفحه است که تیرگی‌اش در لبه‌ی «پایینِ محلیِ» خودش (−y) بیشترین است و تا لبه‌ی بالا (+y) محو
// می‌شود؛ با چرخشِ درونِ‌صفحه‌ای (rotation[2]) جهتِ تیرگی تعیین می‌شود.

const AO_COLOR = '#15110c'; // کمی گرم (نه سیاهِ خالص)، مثلِ سایه‌ی واقعیِ داخلِ خانه
const AO_USERDATA = { khNoRoomLight: true }; // به روشناییِ اتاق (roomLighting.jsx) کاری ندارد
const noRaycast = () => null; // نوارها لمس/کلیک را نمی‌گیرند — انتخابِ دیوار/کف مثلِ قبل کار می‌کند

let gradientTexture = null;
// یک بافتِ گرادیانِ مشترک برای همه‌ی نوارها: پایین = کاملاً تیره، بالا = کاملاً شفاف، با افتِ نرمِ
// (تقریباً درجه‌دو) — مثلِ پخشِ واقعیِ سایه‌ی کنج که نزدیکِ کنج تند و بعد آرام محو می‌شود
export function getGradientTexture() {
  if (gradientTexture) return gradientTexture;
  const h = 128;
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(4, h);
  for (let row = 0; row < h; row += 1) {
    // row 0 = بالای بوم = v=1 (شفاف)؛ row h-1 = پایین = v=0 (تیره)
    const v = 1 - row / (h - 1);
    const a = Math.pow(1 - v, 2.2);
    const c = Math.round(a * 255);
    for (let x = 0; x < 4; x += 1) {
      const i = (row * 4 + x) * 4;
      img.data[i] = c;
      img.data[i + 1] = c;
      img.data[i + 2] = c;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  gradientTexture = new THREE.CanvasTexture(canvas);
  gradientTexture.needsUpdate = true;
  return gradientTexture;
}

// size: [عرض، طول در جهتِ گرادیان] قبل از چرخشِ درونِ‌صفحه‌ای
export function AoStrip({ position, rotation, size, opacity }) {
  if (size[0] <= 0 || size[1] <= 0) return null;
  return (
    <mesh position={position} rotation={rotation} raycast={noRaycast} renderOrder={2}>
      <planeGeometry args={size} />
      <meshBasicMaterial
        color={AO_COLOR}
        alphaMap={getGradientTexture()}
        transparent
        opacity={opacity}
        depthWrite={false}
        polygonOffset
        polygonOffsetFactor={-2}
        polygonOffsetUnits={-2}
        userData={AO_USERDATA}
      />
    </mesh>
  );
}

// اندازه‌ها و شدت‌ها (سانتی‌متر) — عمداً ملایم؛ هدف حسِ عمق است، نه لکه‌ی تیره‌ی دیده‌شدنی
export const AO = {
  floorWidth: 28,
  floorOpacity: 0.3,
  wallBottomHeight: 26,
  wallBottomOpacity: 0.26,
  wallTopHeight: 22,
  wallTopOpacity: 0.2,
  cornerWidth: 22,
  cornerOpacity: 0.2,
};

// چهار نوارِ کفِ یک اتاق، کنارِ دیوارها (تیرگی رو به دیوار). y: ارتفاعِ کمی بالاتر از خودِ کف
// skip: لبه‌هایی که دیوار ندارند (مرزِ بینِ دو تکه‌ی یک فضا) — سایه نمی‌گیرند
export function FloorEdgeAo({ x1, x2, z1, z2, y, skip }) {
  const w = x2 - x1;
  const d = z2 - z1;
  const s = Math.min(AO.floorWidth, w / 3, d / 3);
  if (s <= 0) return null;
  const cx = (x1 + x2) / 2;
  const cz = (z1 + z2) / 2;
  const flat = -Math.PI / 2; // صفحه‌ی افقی رو به بالا
  return (
    <group>
      {/* لبه‌ی z کوچک (تیرگی به سمتِ −z) */}
      {!(skip && skip.z1) && <AoStrip position={[cx, y, z1 + s / 2]} rotation={[flat, 0, Math.PI]} size={[w, s]} opacity={AO.floorOpacity} />}
      {/* لبه‌ی z بزرگ (تیرگی به سمتِ +z) */}
      {!(skip && skip.z2) && <AoStrip position={[cx, y, z2 - s / 2]} rotation={[flat, 0, 0]} size={[w, s]} opacity={AO.floorOpacity} />}
      {/* لبه‌ی x کوچک (تیرگی به سمتِ −x) */}
      {!(skip && skip.x1) && <AoStrip position={[x1 + s / 2, y, cz]} rotation={[flat, 0, -Math.PI / 2]} size={[d, s]} opacity={AO.floorOpacity} />}
      {/* لبه‌ی x بزرگ (تیرگی به سمتِ +x) */}
      {!(skip && skip.x2) && <AoStrip position={[x2 - s / 2, y, cz]} rotation={[flat, 0, Math.PI / 2]} size={[d, s]} opacity={AO.floorOpacity} />}
    </group>
  );
}
